import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, TrendingUp, AlertCircle, Target, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { differenceInDays } from "date-fns";

interface Insight {
  id: string;
  type: "opportunity" | "warning" | "action" | "trend";
  title: string;
  description: string;
  action?: { label: string; path: string };
  priority: "high" | "medium" | "low";
}

const iconMap = {
  opportunity: TrendingUp,
  warning: AlertCircle,
  action: Target,
  trend: Sparkles,
};

const priorityColors = {
  high: "destructive",
  medium: "default",
  low: "secondary",
} as const;

export function AIInsightsPanel() {
  const navigate = useNavigate();
  const { orgId } = useAuth();
  const [insights, setInsights] = useState<Insight[]>([]);

  useEffect(() => {
    if (!orgId) return;

    const generate = async () => {
      const generated: Insight[] = [];

      // Check stale leads (no contact in 5+ days)
      const { data: staleLeads } = await supabase
        .from('leads')
        .select('id, last_contact_date')
        .eq('org_id', orgId)
        .in('status', ['new', 'contacted']);

      const staleCount = staleLeads?.filter(l => {
        if (!l.last_contact_date) return true;
        return differenceInDays(new Date(), new Date(l.last_contact_date)) >= 5;
      }).length || 0;

      if (staleCount > 0) {
        generated.push({
          id: "stale-leads",
          type: "action",
          title: `${staleCount} lead${staleCount > 1 ? 's' : ''} need follow-up`,
          description: `You have leads that haven't been contacted in over 5 days`,
          action: { label: "Review Leads", path: "/leads" },
          priority: "high",
        });
      }

      // Check pending proposals (2+ weeks old)
      const { data: pendingJobs } = await supabase
        .from('jobs')
        .select('id, created_at')
        .eq('org_id', orgId)
        .in('status', ['proposed', 'estimating']);

      const oldProposals = pendingJobs?.filter(j =>
        differenceInDays(new Date(), new Date(j.created_at)) >= 14
      ).length || 0;

      if (oldProposals > 0) {
        generated.push({
          id: "old-proposals",
          type: "warning",
          title: "Proposal delays",
          description: `${oldProposals} proposal${oldProposals > 1 ? 's have' : ' has'} been pending for over 2 weeks`,
          action: { label: "View Jobs", path: "/jobs" },
          priority: "high",
        });
      }

      // Jobs completed this month
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count: completedThisMonth } = await supabase
        .from('jobs')
        .select('id', { count: 'exact', head: true })
        .eq('org_id', orgId)
        .eq('status', 'completed')
        .gte('updated_at', startOfMonth.toISOString());

      if (completedThisMonth && completedThisMonth > 0) {
        generated.push({
          id: "completed-trend",
          type: "trend",
          title: `${completedThisMonth} job${completedThisMonth > 1 ? 's' : ''} completed this month`,
          description: "Keep the momentum going!",
          priority: "medium",
        });
      }

      // New leads this week
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { count: newLeadsCount } = await supabase
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .eq('org_id', orgId)
        .gte('created_at', weekAgo);

      if (newLeadsCount && newLeadsCount > 0) {
        generated.push({
          id: "new-leads",
          type: "opportunity",
          title: `${newLeadsCount} new lead${newLeadsCount > 1 ? 's' : ''} this week`,
          description: "Respond quickly to maximize conversion",
          action: { label: "View Leads", path: "/leads" },
          priority: "medium",
        });
      }

      setInsights(generated.length > 0 ? generated : [{
        id: "all-clear",
        type: "trend",
        title: "All caught up!",
        description: "No urgent items right now. Great work!",
        priority: "low",
      }]);
    };

    generate();
  }, [orgId]);

  return (
    <Card className="backdrop-blur-xl bg-gradient-to-br from-primary/10 via-white/10 to-white/5 dark:from-primary/10 dark:via-black/10 dark:to-black/5 border-white/20">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-primary/20 backdrop-blur-sm">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <CardTitle className="text-lg font-semibold">AI Insights</CardTitle>
          </div>
          <Badge variant="secondary" className="backdrop-blur-sm">Live</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {insights.map((insight) => {
          const Icon = iconMap[insight.type];
          return (
            <div
              key={insight.id}
              className="group p-4 rounded-xl backdrop-blur-sm bg-white/5 hover:bg-white/10 dark:bg-black/5 dark:hover:bg-black/10 border border-white/10 transition-all duration-200"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-sm">{insight.title}</h4>
                    <Badge variant={priorityColors[insight.priority]} className="text-xs">
                      {insight.priority}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {insight.description}
                  </p>
                  {insight.action && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs text-primary"
                      onClick={() => navigate(insight.action!.path)}
                    >
                      {insight.action.label} <Zap className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
